import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { languages, LanguageCode } from '../../i18n';

interface LanguageSwitcherProps {
  /** Called on every pick, even when the chosen language equals the current one
   *  (i18next emits no languageChanged for a same-language switch — ticket 5's
   *  legal pages use this to drop their visit-local ?lang override). */
  onSelect?: (code: LanguageCode) => void;
  /** Effective language to display and mark active. Defaults to i18n.language;
   *  the legal pages pass their ?lang-overridden language so the switcher
   *  reflects what the reader actually sees and lets them leave it. */
  activeCode?: string;
}

export function LanguageSwitcher({ onSelect, activeCode }: LanguageSwitcherProps = {}) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const effectiveCode = activeCode ?? i18n.language;
  const currentLang = languages.find(l => l.code === effectiveCode) || languages[0];

  const handleLanguageChange = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    onSelect?.(code);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isOpen
            ? 'bg-primary/15 text-primary'
            : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden md:inline">{currentLang.name}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 p-1 w-40 bg-popover border border-border rounded-lg shadow-lg z-50">
          {languages.map((lang) => {
            const isActive = effectiveCode === lang.code || effectiveCode.startsWith(lang.code + '-');
            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isActive}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-md transition-colors focus:outline-none
                  ${isActive
                    ? 'bg-primary/15 text-primary font-medium cursor-default'
                    : 'text-muted-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground'
                  }`}
              >
                <span className="flex items-center gap-3">
                  <span>{lang.name}</span>
                </span>
                {isActive && <Check className="w-4 h-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

